import type { ReactNode } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { Box, Flex, xcss } from '@atlaskit/primitives';

export const CharlieFontStack =
	"'Charlie Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Noto Sans', 'Ubuntu', 'Droid Sans', 'Helvetica Neue', sans-serif;";

const SfProStack = `SF Pro Text, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`;

export const EndOfTrialModalBody = ({ children }: { children: JSX.Element[] }) => (
	<Box backgroundColor="elevation.surface">
		<Flex>{children}</Flex>
	</Box>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LeftSide = styled.div({
	padding: token('space.800', '60px'),
	display: 'flex',
	flexDirection: 'column',
	rowGap: token('space.400', '32px'),
	justifyContent: 'center',
	flexGrow: 1,
	flexBasis: 0,
	maxWidth: '565px',
	backgroundColor: token('elevation.surface'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RightSide = styled.div({
	padding: `0 ${token('space.400', '34px')}`,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	rowGap: token('space.300', '28px'),
	backgroundColor: token('color.background.accent.blue.subtler'),
	flexGrow: 1,
	flexBasis: 0,
	maxWidth: '565px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EndOfTrialModalHeading = styled.div({
	color: token('color.text'),
	fontFamily: CharlieFontStack,
	fontSize: '35px',
	lineHeight: '40px',
	fontWeight: 500,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EndOfTrialModalText = styled.div({
	color: token('color.text'),
	fontFamily: CharlieFontStack,
	fontSize: '16px',
	lineHeight: '20px',
	fontStyle: 'normal',
	width: '445px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BenefitsList = styled.div({
	display: 'flex',
	flexDirection: 'column',
	rowGap: token('space.300', '24px'),
	// these aren't even grossly similar, but it's splitting the difference between what figma says and what tokens are available
	paddingBottom: token('space.1000', '95px'),
});

export const BenefitContainer = ({ children }: { children: ReactNode }) => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	<Flex alignItems="center" xcss={xcss({ paddingLeft: 'space.200' })}>
		{children}
	</Flex>
);

// this feels overly complicated but I can't think of a better way to do it at the moment
export const BenefitIconStyled = ({ children }: { children: ReactNode }) => (
	<Flex alignItems="center" justifyContent="center">
		<Box
			padding="space.100"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			xcss={xcss({
				height: '40px',
				width: '40px',
				marginRight: 'space.200',
				borderRadius: 'border.radius.circle',
				backgroundColor: 'color.background.accent.gray.subtlest',
			})}
		>
			{children}
		</Box>
	</Flex>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CircleSvg = styled.img({
	paddingRight: token('space.200', '16px'),
	height: '40px',
	width: '40px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BenefitTitle = styled.div({
	fontSize: '14px',
	lineHeight: '20px',
	fontWeight: 700,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BenefitExplanation = styled.div({
	fontSize: '14px',
	lineHeight: '20px',
	fontWeight: 400,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ButtonsContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	columnGap: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SlideContainer = styled.div({
	paddingBottom: token('space.100', '8px'),
	maxWidth: '500px',
	maxHeight: '400px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CarouselSlide = styled.img({
	maxHeight: '400px',
	maxWidth: '500px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SlideTextStyled = styled.div({
	fontFamily: CharlieFontStack,
	fontSize: '20px',
	fontStyle: 'normal',
	fontWeight: 500,
	lineHeight: '24px',
	color: token('color.text'),
	textAlign: 'center',
	paddingBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CarouselButtonStyled = styled.button({
	border: 'none',
	backgroundColor: 'transparent',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CarouselControlsStyled = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	rowGap: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Bolded = styled.span({
	fontWeight: 600,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DeactivationWarningStyled = styled.div({
	paddingTop: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DeactivationText = styled.div({
	fontFamily: CharlieFontStack,
	fontWeight: 500,
	size: '12px',
	lineHeight: '20px',
	paddingLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RemoveUsersLink = styled.a({
	color: token('color.link', 'blue'),
	border: 'none',
	backgroundColor: 'transparent',
	fontFamily: CharlieFontStack,
	fontWeight: 500,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TimelineStyled = styled.div({
	marginBottom: token('space.1000'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TimelineDayTitle = styled.div({
	height: 16,
	fontWeight: 600,
	size: 14,
	fontFamily: SfProStack,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TimelineDayBody = styled.div({
	size: '11px',
	height: 14,
	fontWeight: 400,
	fontFamily: SfProStack,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TimelineDayIcon = styled.div({
	color: token('color.text.brand'),
	padding: token('space.025'),
});

export const TimelineIconStyled = ({ children }: { children: ReactNode }) => (
	<Box
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		xcss={xcss({
			height: '26px',
			width: '26px',
			border: '2px solid',
			borderRadius: 'border.radius.circle',
			borderColor: 'color.border.brand',
		})}
	>
		<TimelineDayIcon>
			<Flex alignItems="center" justifyContent="center">
				{children}
			</Flex>
		</TimelineDayIcon>
	</Box>
);

export const FinalTimelineIconStyled = ({ children }: { children: ReactNode }) => (
	<Box
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		xcss={xcss({
			height: '26px',
			width: '26px',
			border: '2px solid',
			borderRadius: 'border.radius.circle',
			borderColor: 'color.border',
		})}
	>
		<TimelineDayIcon>
			<Flex alignItems="center" justifyContent="center">
				{children}
			</Flex>
		</TimelineDayIcon>
	</Box>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DashedLine = styled.div({
	height: 36,
	width: 2,
	marginLeft: token('space.150'),
	marginTop: token('space.negative.050'),
	marginBottom: token('space.negative.050'),
	borderLeft: '2px dashed',
	color: token('color.border.input'),
});
